import { VpcPeering, VpcPeeringFactory } from '@hevoio/hevo-ui-toolkit';
import { useEffect, useMemo, useState } from 'react';
import { EMPTY } from 'rxjs';
import TeamAPI from '../../containers/team/api/TeamAPI';
import { VPCPeeringConnection } from '../../containers/team/Connectivity/interface';
import useService from '../../injector/hooks/useService';
import { isVpcPeeringEligible } from '../constants/vpcPeering';
import { TeamSettingsService } from '../services/team-settings.service';
import { UserService } from '../services/user.service';
import { vpcPeeringFromConnections } from '../services/vpc-peering.service';
import { useObservableState } from './useObservableState';

interface UseVpcPeeringSpecProps {
  /**
   * The form's real source or destination type identifier — `AWS_RDS_POSTGRES`,
   * `REDSHIFT`, and so on. NOT the display string `entityTypeIdentifier` carries.
   */
  entityType?: string;
}

/**
 * Absent is the contract. Anything other than a usable spec — an ineligible
 * entity type, a team with no `vpc_peering_host`, a host mapping with no entry
 * for this deployment, or settings that have not booted yet — resolves to
 * undefined, which is the same value the Edge template carries when the key is
 * missing. One code path, and the SSH block renders exactly as it did before
 * peering existed.
 *
 * Standard's source is the team setting PLUS the customer-facing peering list
 * (INF-1743, "Backend contract — Standard"), NOT
 * `GET /config/v2.0/connectivity/vpc-peering` — that endpoint is not available,
 * which is what unsettles the ticket's decisions 2 and 3. Edge is untouched: it
 * reads the server-computed `vpc_peering` key off its connector template.
 *
 *  1. `vpc_peering_host` is the flag and the host. No entry for this deployment
 *     means no peering, and no request is issued — a team without peering costs
 *     zero connectivity-service calls, same as the ineligible-type case.
 *  2. `GET /connectivity-service/api/peering/requests` supplies the routes and
 *     their acceptance state, collapsed by `vpcPeeringFromConnections`.
 *
 * Eligibility is gated HERE by `isVpcPeeringEligible`, because no backend
 * filters this path by entity type. Listed types get the peering toggle
 * alongside SSH; every other SSH-capable form keeps manual SSH only. The team
 * setting is team-wide, so without the list a GCP or Azure source would be
 * offered a tunnel that cannot reach it and would fail late, as an opaque SSH
 * timeout at test-connection.
 *
 * Fail-open, per the ticket's AC: a rejected or empty list never surfaces as a
 * form error and never withdraws the toggle. It falls back to the tunnel the
 * team setting already names, with no routes to render — which is what the
 * setting alone did before this ticket.
 */
/**
 * How long to wait for `/peering/requests` before falling through to the
 * team-setting tunnel. Not a request timeout — the response is still used if it
 * arrives later; this only stops an unanswered request from holding the form's
 * toggles hostage.
 */
const PEERING_LIST_DEADLINE_MS = 5000;

export default function useVpcPeeringSpec({ entityType }: UseVpcPeeringSpecProps): {
  peering: VpcPeering | undefined;
  loading: boolean;
} {
  const teamSettingsService = useService(TeamSettingsService);
  // Read through DI rather than useUserService, which calls getUser() eagerly:
  // SSHConfig renders inside every connector form, including ones whose tests
  // provide a partial container. No team id simply means no list request, which
  // lands on the team-setting fallback like any other absent answer.
  const userService = useService(UserService);
  const teamId = userService?.getUser?.()?.teamId;
  const eligible = isVpcPeeringEligible(entityType);

  // A connector form can mount before the boot fetch lands, and
  // getVPCPeeringConfig() is a point read that cannot see it arrive. Subscribing
  // re-derives the host the moment settings resolve, so the toggle appears
  // without the customer reloading the form.
  const settings$ = useMemo(
    () => teamSettingsService?.teamSettings$ ?? EMPTY,
    [teamSettingsService]
  );
  // Optional calls throughout: connector-form tests provide partial containers,
  // and a service that cannot answer is the same as one answering "no peering".
  // `unknown`, not `any`: the blob is only ever read for truthiness (below and
  // in `loading`), so nothing needs the escape hatch.
  const [teamSettings] = useObservableState<unknown>(
    settings$,
    teamSettingsService?.getTeamSettingsValue?.()
  );

  // Reuses the service's existing parse of the region -> host JSON map, so the
  // `__DEPLOYMENT_ENV__` lookup and its raw-string fallback stay in one place.
  const teamHost = useMemo(
    () =>
      eligible && teamSettings ? teamSettingsService?.getVPCPeeringConfig?.()?.host : null,
    [eligible, teamSettings, teamSettingsService]
  );

  const [connections, setConnections] = useState<VPCPeeringConnection[] | undefined>(undefined);
  const [listSettled, setListSettled] = useState(false);
  // Distinct from an empty list. An answer saying "no peering" is information;
  // no answer at all is not, and the two must not render the same.
  const [listUnanswered, setListUnanswered] = useState(false);

  useEffect(() => {
    if (!teamHost || !teamId) {
      setConnections(undefined);
      setListSettled(false);
      setListUnanswered(false);
      return undefined;
    }

    let cancelled = false;

    setListSettled(false);
    setListUnanswered(false);

    // Fail-open has to cover silence, not just errors. The request carries no
    // timeout, so a connectivity-service that accepts the connection and never
    // answers leaves `listSettled` false forever — and the form gates both
    // toggles on it, so nothing renders and the customer cannot configure SSH
    // at all. A refused or failing request is fine: `.catch` runs and
    // `.finally` settles. This bounds the case where neither does.
    const deadline = setTimeout(() => {
      if (cancelled) return;
      setListUnanswered(true);
      setListSettled(true);
    }, PEERING_LIST_DEADLINE_MS);

    // No `hevoRegion`: the param filters the list server-side, and the app has
    // no notion of a current region to filter by — `hevo_region` is a field
    // *on* each connection, not app state. Filtering would hide a connection
    // in another region from a connector that can legitimately use it, so the
    // form reads the whole list. The two Connectivity call sites omit it too.
    TeamAPI.getConnectivityPeeringList(teamId)
      .then((res: { data: VPCPeeringConnection[] }) => {
        if (!cancelled) setConnections(res?.data);
      })
      .catch(() => {
        // Deliberately silent: a connector form must never surface a peering
        // lookup failure as a form error. It withdraws the toggle rather than
        // offering a tunnel nothing has confirmed — the same thing Edge does,
        // where an unreachable connectivity-service simply means no
        // `vpc_peering` key on the template. Manual SSH is untouched.
        if (cancelled) return;
        setConnections(undefined);
        setListUnanswered(true);
      })
      .finally(() => {
        clearTimeout(deadline);
        if (!cancelled) setListSettled(true);
      });

    return () => {
      cancelled = true;
      clearTimeout(deadline);
    };
  }, [teamHost, teamId]);

  const peering = useMemo(() => {
    if (!teamHost) {
      return undefined;
    }

    // Nothing until the list has settled. `connections` is undefined both
    // before the request and after a failed one, and `vpcPeeringFromConnections`
    // reads that as "no usable row" and falls back to the team-setting tunnel —
    // AVAILABLE, with no routes. Served during the in-flight window that is a
    // spec the backend never claimed: the create default fires on it and writes
    // the triple, and when the real list comes back PENDING_ACCEPTANCE the
    // toggle drops to off while `useSSH` stays on, leaving the customer looking
    // at Hevo's tunnel credentials in editable manual-SSH fields.
    //
    // Fail-open is unaffected: the catch sets `connections` undefined and the
    // finally settles the flag, so a failed lookup still reaches the fallback.
    if (teamId && !listSettled) {
      return undefined;
    }

    // Asked and got nothing back. Offering the team-setting tunnel here would
    // put a toggle in front of the customer for a tunnel whose routes,
    // acceptance state and reachability are all unknown.
    if (listUnanswered) {
      return undefined;
    }

    const raw = vpcPeeringFromConnections(connections, teamHost);

    return raw ? VpcPeeringFactory(raw) : undefined;
  }, [connections, teamHost, teamId, listSettled, listUnanswered]);

  return {
    peering,
    // True only while there is something outstanding: team settings booting, or
    // the peering list in flight for a team the setting says has a tunnel.
    loading: eligible && (!teamSettings || (!!teamHost && !!teamId && !listSettled))
  };
}
